import { useState, useMemo } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowUpDown, Settings2, Check, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ProductHistoryPopover from "@/components/ProductHistoryPopover";
import axios from "axios";
import { toast } from "sonner";

import { API_URL as API } from "@/lib/api";

const ALL_COLUMNS = [
  { key: "codigo", label: "Codigo", default: true },
  { key: "descripcion", label: "Descripcion", default: true },
  { key: "familia", label: "Familia", default: true },
  { key: "subfamilia", label: "SubFamilia", default: true },
  { key: "unidad", label: "Unidad", default: true },
  { key: "cantidad_inicial", label: "C.Inicial", default: true },
  { key: "ingreso", label: "Ingreso", default: true },
  { key: "salida", label: "Salida", default: true },
  { key: "stock", label: "Stock", default: true },
  { key: "precio_usd", label: "P.USD", default: true },
  { key: "pmenor", label: "PMenor", default: true },
  { key: "pmayor", label: "PMayor", default: true },
  { key: "proveedor", label: "Proveedor", default: false },
  { key: "lote", label: "Lote", default: false },
  { key: "cod_barra", label: "Cod.Barra", default: false },
  { key: "stock_minimo", label: "Stock Min.", default: false },
  { key: "estado", label: "Estado", default: true },
];

const MONETARY_COL_KEYS = ['precio_usd', 'pmenor', 'pmayor'];

const ProductTable = ({ products, loading, totalCount, tipoCambio, canEdit = false, showHistory = false, onEdit, onRefresh, userRole = "admin" }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterAlerta, setFilterAlerta] = useState(null);
  const availableColumns = useMemo(() =>
    userRole === "viewer" ? ALL_COLUMNS.filter(c => !MONETARY_COL_KEYS.includes(c.key)) : ALL_COLUMNS
  , [userRole]);
  const [visibleCols, setVisibleCols] = useState(() =>
    ALL_COLUMNS.filter(c => c.default).map(c => c.key)
  );

  const toggleColumn = (key) => {
    setVisibleCols(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const isVisible = (key) => visibleCols.includes(key) && availableColumns.some(c => c.key === key);

  const formatPrice = (price) =>
    new Intl.NumberFormat('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price) + ' Bs';

  const formatUSD = (usd) => {
    return '$ ' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(usd || 0);
  };

  const getEstadoBadge = (product) => {
    const estado = product.estado || "";
    const st = product.sell_through;
    const stLabel = st != null ? ` (${st}%)` : "";
    switch (estado) {
      case "Agotado":
        return { label: "Agotado", className: "bg-red-600/15 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800" };
      case "Por Agotar / Reordenar":
        return { label: `Reordenar${stLabel}`, className: "bg-orange-500/15 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800" };
      case "Alta Rotacion":
        return { label: `Alta Rot.${stLabel}`, className: "bg-emerald-500/15 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800" };
      case "Rotacion Saludable":
        return { label: `Saludable${stLabel}`, className: "bg-green-500/15 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800" };
      case "Nuevo Ingreso":
        return { label: "Nuevo Ingreso", className: "bg-sky-500/15 text-sky-700 border-sky-300 dark:bg-sky-900/30 dark:text-sky-400 dark:border-sky-800" };
      case "Baja Rotacion / Remanente":
        return { label: `Baja Rot.${stLabel}`, className: "bg-amber-500/15 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800" };
      case "Rotacion Regular":
        return { label: `Regular${stLabel}`, className: "bg-slate-500/15 text-slate-700 border-slate-300 dark:bg-slate-900/30 dark:text-slate-400 dark:border-slate-700" };
      default:
        if (product.stock === 0) return { label: "Sin Stock", className: "bg-red-600/15 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800" };
        if (product.stock < 10) return { label: "Stock Bajo", className: "bg-amber-500/15 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800" };
        return null;
    }
  };

  const alertaCount = useMemo(() => products.filter(p => p.alerta_reposicion).length, [products]);
  const minimoCount = useMemo(() => products.filter(p => p.alerta_stock_minimo).length, [products]);

  const displayProducts = useMemo(() => {
    if (filterAlerta === "reposicion") return products.filter(p => p.alerta_reposicion);
    if (filterAlerta === "minimo") return products.filter(p => p.alerta_stock_minimo);
    return products;
  }, [products, filterAlerta]);

  const handleSort = (key) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const handleDelete = async (codigo) => {
    if (!window.confirm(`Eliminar producto '${codigo}'? Esta accion no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/api/products/crud/${encodeURIComponent(codigo)}`);
      toast.success(`Producto '${codigo}' eliminado`);
      onRefresh?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al eliminar");
    }
  };

  const sortedProducts = [...displayProducts].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    if (typeof aValue === 'string') return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
  });

  if (loading) {
    return (
      <Card className="p-8 text-center border border-border bg-card shadow-sm">
        <p className="body-font text-muted-foreground">Cargando productos...</p>
      </Card>
    );
  }

  const SortHeader = ({ field, label }) => (
    <TableHead className="table-header-amazon font-semibold">
      <Button variant="ghost" size="sm" onClick={() => handleSort(field)} className="text-white hover:text-[#FF9900] hover:bg-white/10 text-xs body-font p-1 h-auto" data-testid={`sort-${field}`}>
        {label} <ArrowUpDown className="ml-1 h-3 w-3" />
      </Button>
    </TableHead>
  );

  return (
    <Card className="border border-border bg-card shadow-sm overflow-hidden" data-testid="product-table-card">
      <div className="p-4 border-b border-border flex justify-between items-center">
        <h2 className="heading-font text-lg font-bold text-foreground">Productos</h2>
        <div className="flex items-center gap-3">
          {alertaCount > 0 && (
            <Button
              variant={filterAlerta === "reposicion" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterAlerta(f => f === "reposicion" ? null : "reposicion")}
              className={`text-xs body-font gap-1.5 ${filterAlerta === "reposicion" ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'text-orange-700 border-orange-300 hover:bg-orange-50 dark:text-orange-400 dark:border-orange-700 dark:hover:bg-orange-900/20'}`}
              data-testid="filter-alerta-reposicion"
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              Reordenar ({alertaCount})
            </Button>
          )}
          {minimoCount > 0 && (
            <Button
              variant={filterAlerta === "minimo" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterAlerta(f => f === "minimo" ? null : "minimo")}
              className={`text-xs body-font gap-1.5 ${filterAlerta === "minimo" ? 'bg-red-600 hover:bg-red-700 text-white' : 'text-red-700 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/20'}`}
              data-testid="filter-alerta-stock-minimo"
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              Bajo minimo ({minimoCount})
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs body-font" data-testid="column-visibility-toggle">
                <Settings2 className="h-3.5 w-3.5 mr-1.5" /> Columnas ({visibleCols.length})
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 p-2 max-h-80 overflow-y-auto">
              {availableColumns.map(col => (
                <button
                  key={col.key}
                  onClick={() => toggleColumn(col.key)}
                  className="flex items-center w-full px-2 py-1.5 text-xs rounded hover:bg-muted transition-colors body-font"
                  data-testid={`col-toggle-${col.key}`}
                >
                  <div className={`w-4 h-4 mr-2 rounded border flex items-center justify-center ${
                    isVisible(col.key) ? 'bg-[#FF9900] border-[#FF9900]' : 'border-border'
                  }`}>
                    {isVisible(col.key) && <Check className="h-3 w-3 text-white" />}
                  </div>
                  {col.label}
                </button>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <p className="body-font text-sm text-muted-foreground" data-testid="product-count">
            {displayProducts.length} de {totalCount}{filterAlerta ? " (filtrado)" : ""}
          </p>
        </div>
      </div>

      <div className="overflow-x-auto custom-scrollbar">
        <Table>
          <TableHeader>
            <TableRow className="border-0">
              {isVisible("codigo") && <SortHeader field="codigo" label="Codigo" />}
              {isVisible("descripcion") && <SortHeader field="descripcion" label="Descripcion" />}
              {isVisible("familia") && <SortHeader field="familia" label="Familia" />}
              {isVisible("subfamilia") && <SortHeader field="subfamilia" label="SubFamilia" />}
              {isVisible("unidad") && <TableHead className="table-header-amazon body-font font-semibold text-xs text-white">Unidad</TableHead>}
              {isVisible("cantidad_inicial") && <SortHeader field="cantidad_inicial" label="C.Ini" />}
              {isVisible("ingreso") && <SortHeader field="ingreso" label="Ing" />}
              {isVisible("salida") && <SortHeader field="salida" label="Sal" />}
              {isVisible("stock") && <SortHeader field="stock" label="Stock" />}
              {isVisible("precio_usd") && <SortHeader field="precio_usd" label="P.USD" />}
              {isVisible("pmenor") && <SortHeader field="pmenor" label="PMenor" />}
              {isVisible("pmayor") && <SortHeader field="pmayor" label="PMayor" />}
              {isVisible("proveedor") && <TableHead className="table-header-amazon body-font font-semibold text-xs text-white">Proveedor</TableHead>}
              {isVisible("lote") && <SortHeader field="lote" label="Lote" />}
              {isVisible("cod_barra") && <TableHead className="table-header-amazon body-font font-semibold text-xs text-white">Cod.Barra</TableHead>}
              {isVisible("stock_minimo") && <SortHeader field="stock_minimo" label="Stock Min." />}
              {isVisible("estado") && <TableHead className="table-header-amazon body-font font-semibold text-xs text-white">Estado</TableHead>}
              {canEdit && <TableHead className="table-header-amazon body-font font-semibold text-xs text-white text-right sticky right-0 z-10">Acciones</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleCols.length + (canEdit ? 1 : 0)} className="text-center py-8">
                  <p className="body-font text-muted-foreground">No se encontraron productos</p>
                </TableCell>
              </TableRow>
            ) : (
              sortedProducts.map((product, index) => {
                const estadoBadge = getEstadoBadge(product);
                const isOdd = index % 2 === 1;
                return (
                  <TableRow
                    key={product.codigo + '-' + index}
                    className={`table-row-hover border-b border-border/50 ${
                      product.stock === 0 ? 'bg-red-50/50 dark:bg-red-900/10' :
                      product.alerta_reposicion ? 'bg-orange-50/40 dark:bg-orange-900/10' :
                      product.stock < 10 ? 'bg-amber-50/50 dark:bg-amber-900/10' :
                      isOdd ? 'bg-muted/30' : 'bg-card'
                    } hover:bg-[#FF9900]/5`}
                    data-testid={`product-row-${index}`}
                  >
                    {isVisible("codigo") && <TableCell className="mono-font text-xs font-medium text-foreground" data-testid={`product-codigo-${index}`}>{product.codigo}</TableCell>}
                    {isVisible("descripcion") && <TableCell className="body-font text-xs font-medium text-foreground" data-testid={`product-descripcion-${index}`}>
                      <span className="block max-w-xs leading-snug line-clamp-2">{product.descripcion}</span>
                    </TableCell>}
                    {isVisible("familia") && <TableCell className="body-font text-xs text-muted-foreground">{product.familia}</TableCell>}
                    {isVisible("subfamilia") && <TableCell className="body-font text-xs text-muted-foreground">{product.subfamilia}</TableCell>}
                    {isVisible("unidad") && <TableCell className="body-font text-xs text-muted-foreground">{product.unidad}</TableCell>}
                    {isVisible("cantidad_inicial") && <TableCell className="mono-font text-xs text-center">{product.cantidad_inicial}</TableCell>}
                    {isVisible("ingreso") && <TableCell className="mono-font text-xs text-center text-emerald-600 dark:text-emerald-400">{product.ingreso > 0 ? `+${product.ingreso}` : product.ingreso}</TableCell>}
                    {isVisible("salida") && <TableCell className="mono-font text-xs text-center text-red-600 dark:text-red-400">{product.salida > 0 ? `-${product.salida}` : product.salida}</TableCell>}
                    {isVisible("stock") && <TableCell className="mono-font text-xs font-bold text-center text-foreground" data-testid={`product-stock-${index}`}>{product.stock}</TableCell>}
                    {isVisible("precio_usd") && <TableCell className="mono-font text-xs text-[#FF9900] font-semibold" data-testid={`product-precio-usd-${index}`}>{formatUSD(product.precio_usd)}</TableCell>}
                    {isVisible("pmenor") && <TableCell className="mono-font text-xs text-foreground">{formatPrice(product.pmenor)}</TableCell>}
                    {isVisible("pmayor") && <TableCell className="mono-font text-xs text-foreground">{formatPrice(product.pmayor)}</TableCell>}
                    {isVisible("proveedor") && <TableCell className="body-font text-xs text-muted-foreground">{product.proveedor}</TableCell>}
                    {isVisible("lote") && <TableCell className="mono-font text-xs">{product.lote}</TableCell>}
                    {isVisible("cod_barra") && <TableCell className="mono-font text-xs">{product.cod_barra}</TableCell>}
                    {isVisible("stock_minimo") && <TableCell className={`mono-font text-xs text-center font-semibold ${product.alerta_stock_minimo ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`} data-testid={`product-stock-minimo-${index}`}>{product.stock_minimo}</TableCell>}
                    {isVisible("estado") && (
                      <TableCell>
                        <div className="flex items-center gap-1 flex-wrap">
                          {estadoBadge && <Badge className={`${estadoBadge.className} body-font text-[10px] border whitespace-nowrap`} data-testid={`product-status-${index}`}>{estadoBadge.label}</Badge>}
                          {product.alerta_reposicion && <Badge className="bg-orange-600/15 text-orange-700 border-orange-400 body-font text-[10px] border dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700" data-testid={`product-alerta-${index}`}>Reponer</Badge>}
                          {product.alerta_stock_minimo && <Badge className="bg-red-600/15 text-red-700 border-red-400 body-font text-[10px] border dark:bg-red-900/30 dark:text-red-400 dark:border-red-700" data-testid={`product-alerta-minimo-${index}`}>Bajo min. ({product.stock_minimo})</Badge>}
                          {product.en_liquidacion && <Badge className="bg-violet-600/15 text-violet-700 border-violet-400 body-font text-[10px] border dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-700" data-testid={`product-liquidacion-${index}`}>Liq. -{product.descuento_pct}%</Badge>}
                          {product.manual_edit && <Badge className="bg-[#FF9900]/10 text-[#FF9900] border-[#FF9900]/30 body-font text-[10px] border" data-testid={`product-manual-${index}`}>Manual</Badge>}
                          {showHistory && product.manual_edit && <ProductHistoryPopover codigo={product.codigo} />}
                        </div>
                      </TableCell>
                    )}
                    {canEdit && (
                      <TableCell className="text-right whitespace-nowrap sticky right-0 bg-card z-10 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]">
                        <Button size="sm" variant="ghost" onClick={() => onEdit?.(product)} data-testid={`edit-product-${index}`} className="h-7 w-7 p-0 hover:text-[#FF9900]">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(product.codigo)} data-testid={`delete-product-${index}`} className="h-7 w-7 p-0 hover:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};

export default ProductTable;
